import {
  Accordion,
  Box,
  Button,
  Link,
  Spinner,
  Text,
} from '@nimbus-ds/components';
import {
  BoxPackedIcon,
  CheckCircleIcon,
  CreditCardIcon,
  RedoIcon,
  RocketIcon,
  TagIcon,
  ToolsIcon,
  TruckIcon,
  UploadIcon,
} from '@nimbus-ds/icons';
import { Trans } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link as LinkRoute } from 'react-router-dom';
import { OnboardingTask } from '@tiendanube/common';
import { Country } from '@tiendanube/common/src/enums';
import { PROJECT_SHIVA_TAG } from 'App/featuresFlags';
import { useHasTags } from 'App/hooks';
import { trackingDashboardSeeStoreClick } from 'App/tracking';
import { ExternalLink, IconBox, Stack, SubmitButton } from 'commons/components';
import { getStoreURL } from 'domains/Auth/authSlice/authSelectors';
import {
  useGetCountry,
  useTransactionFeeAdminDisabled,
} from 'domains/Auth/hooks';
import { TRANSACTION_FEES_BASE_ROUTE } from 'domains/Billing/TransactionFees/transactionFeesRoutes';
import { useTranslationDashboard } from 'domains/Dashboard/hooks';
import { trackingSelectThemeClick } from 'domains/Dashboard/tracking';
import { useOnboardingTasksConfig } from './hooks';
import Skeleton from './Skeleton';
import './OnboardingAccordionTasks.scss';

interface OnboardingAccordionProps {
  items: OnboardingTask[];
}

const ONBOARDING_TASK_ORDER = {
  store_created: 0,
  setup_first_product: 1,
  setup_layout: 2,
  setup_multicd: 3,
  setup_shipping: 4,
  setup_payments: 5,
  setup_meli_sync: 6,
};

const SECONDARY_LOG_EVENTS = {
  setup_layout: trackingSelectThemeClick,
};

function OnboardingTaskAccordion({
  items,
}: Readonly<OnboardingAccordionProps>) {
  const isBlocked = useTransactionFeeAdminDisabled();
  const url = useSelector(getStoreURL);
  const [isProjectShiva] = useHasTags([PROJECT_SHIVA_TAG]);
  const t = useTranslationDashboard();
  const storeCountry = useGetCountry();
  const isArgOrBr = [Country.BR, Country.AR].includes(storeCountry);

  const ICONS = {
    setup_layout: <ToolsIcon />,
    setup_first_product: isProjectShiva ? <UploadIcon /> : <TagIcon />,
    setup_payments: <CreditCardIcon />,
    setup_shipping: <TruckIcon />,
    setup_multicd: <BoxPackedIcon />,
    store_created: <TruckIcon />,
    setup_meli_sync: <RedoIcon />,
    setup_testing_sale: <RocketIcon />,
  };

  const sortedItems = [...items]
    .filter((item) => item.name !== 'setup_shipping_nuvemenvio')
    .sort(
      (a, b) => ONBOARDING_TASK_ORDER[a.name] - ONBOARDING_TASK_ORDER[b.name],
    );

  const selectedDefault = sortedItems.findIndex(
    ({ name, completed }) =>
      !['store_created', 'pos_store_created'].includes(name) && !completed,
  );

  const {
    taskConfig,
    goToConfig,
    trackEvent,
    isTaskLoading,
    isLoadingPayments,
    isLoadingShipping,
    isLoadingMeliSync,
  } = useOnboardingTasksConfig(sortedItems);

  return (
    <div className="stratus--onboarding-accordion-tasks">
      <Accordion
        selectedDefault={`${selectedDefault > 0 ? selectedDefault : 1}`}
      >
        {sortedItems.map(
          (
            {
              name,
              title,
              ctaText,
              ctaLink,
              completed,
              secondaryCtaLink,
              secondaryCtaText,
              description,
              ctaExternal,
            },
            index,
          ) =>
            name === 'store_created' || name === 'pos_store_created' ? (
              <Accordion.Item key={name} index={`${index}`}>
                <div className="stratus--store-created-item">
                  <Stack align="stretch" justify="space-between">
                    <Stack spacing="none">
                      <IconBox
                        backgroundColor="success-surfaceHighlight"
                        color="primary-textLow"
                        borderRadius="full"
                        height="24px"
                        width="24px"
                        marginRight="2-5"
                      >
                        <CheckCircleIcon color="currentColor" />
                      </IconBox>
                      <Stack column align="flex-start" spacing="none">
                        <Text fontWeight="medium" color="neutral-textHigh">
                          <s>{title}</s>
                        </Text>
                        <Text fontSize="caption">{description}</Text>
                      </Stack>
                    </Stack>
                    {name === 'store_created' && (
                      <div className="stratus--store-created-link">
                        <ExternalLink
                          href={isBlocked ? TRANSACTION_FEES_BASE_ROUTE : url}
                          textDecoration="none"
                          appearance="primary"
                          fontSize="caption"
                          onClick={trackingDashboardSeeStoreClick}
                        >
                          {ctaText}
                        </ExternalLink>
                      </div>
                    )}
                  </Stack>
                </div>
              </Accordion.Item>
            ) : (
              <Accordion.Item key={name} index={`${index}`}>
                <Accordion.Header borderTop="base">
                  <Box width="100%" display="flex" alignItems="center">
                    <Box>
                      <IconBox
                        backgroundColor={
                          completed
                            ? 'success-surfaceHighlight'
                            : 'primary-surface'
                        }
                        color={completed ? 'currentColor' : 'primary-textLow'}
                        borderRadius="full"
                        height="24px"
                        width="24px"
                        marginRight="2-5"
                      >
                        {completed ? (
                          <CheckCircleIcon color="currentColor" />
                        ) : (
                          ICONS[name]
                        )}
                      </IconBox>
                    </Box>
                    <Text fontWeight="medium" color="neutral-textHigh">
                      {completed ? <s>{title}</s> : title}
                    </Text>
                  </Box>
                </Accordion.Header>
                <Accordion.Body padding="none">
                  <Box width="85%" paddingLeft="12" paddingBottom="4">
                    <Text fontSize="caption" textAlign="left">
                      {isProjectShiva &&
                      isArgOrBr &&
                      name === 'setup_payments' ? (
                        <Trans
                          t={t}
                          i18nKey="shiva.setup_payments.description"
                          components={{
                            strong: <strong />,
                          }}
                        />
                      ) : (
                        description
                      )}
                    </Text>
                    <Box
                      paddingTop="1-5"
                      display="flex"
                      justifyContent="flex-start"
                    >
                      <Box marginRight="4">
                        {[
                          'setup_payments',
                          'setup_shipping',
                          'setup_testing_sale',
                          'setup_meli_sync',
                        ].includes(name) ? (
                          <SubmitButton
                            appearance="primary"
                            onClick={goToConfig(ctaLink, name)}
                            disabled={isTaskLoading(name)}
                          >
                            {ctaText}
                          </SubmitButton>
                        ) : (
                          <Button
                            as={LinkRoute}
                            appearance="primary"
                            to={
                              isBlocked ? TRANSACTION_FEES_BASE_ROUTE : ctaLink
                            }
                            onClick={trackEvent(ctaLink, name)}
                          >
                            {ctaText}
                          </Button>
                        )}
                      </Box>
                      {[
                        'setup_payments',
                        'setup_shipping',
                        'setup_meli_sync',
                      ].includes(name) ? (
                        <Link
                          fontSize="caption"
                          textDecoration="none"
                          appearance="primary"
                          as="button"
                          disabled={
                            isLoadingMeliSync ||
                            isLoadingShipping ||
                            isLoadingPayments
                          }
                          onClick={taskConfig(name, secondaryCtaLink)}
                        >
                          {secondaryCtaText}
                          {(isLoadingMeliSync || isLoadingShipping) && (
                            <Spinner color="currentColor" size={10} />
                          )}
                        </Link>
                      ) : (
                        <Link
                          fontSize="caption"
                          textDecoration="none"
                          appearance="primary"
                          as={name === 'setup_meli_sync' ? 'button' : 'a'}
                          target={ctaExternal ? '_blank' : '_self'}
                          disabled={isLoadingMeliSync}
                          href={
                            isBlocked
                              ? TRANSACTION_FEES_BASE_ROUTE
                              : secondaryCtaLink
                          }
                          onClick={SECONDARY_LOG_EVENTS[name]}
                        >
                          {secondaryCtaText}
                          {isLoadingMeliSync && (
                            <Spinner color="currentColor" size={10} />
                          )}
                        </Link>
                      )}
                    </Box>
                  </Box>
                </Accordion.Body>
              </Accordion.Item>
            ),
        )}
      </Accordion>
    </div>
  );
}

OnboardingTaskAccordion.Skeleton = Skeleton;

export default OnboardingTaskAccordion;
